import type { ContractDetailResult } from "../../domain/types";
import {
  formatDateLocal,
  formatDateTimeLocal,
} from "@/shared/lib/date-time";

interface ContractPdfPreviewContentProps {
  contract: ContractDetailResult;
}

export function ContractPdfPreviewContent({ contract }: ContractPdfPreviewContentProps) {
  return (
    <div
      id="contract-pdf-content"
      className="mx-auto max-w-4xl bg-white p-8 font-serif text-gray-900 leading-relaxed print:max-w-none print:p-0 print:font-serif print:text-gray-900 print:leading-relaxed"
      style={{
        fontFamily: '"Times New Roman", Times, serif',
        lineHeight: "1.5",
        color: "#1a1a1a",
      }}
    >
      {/* PDF Header */}
      <div className="mb-8 border-gray-800 border-b-4 pb-6 print:mb-6">
        <div className="mb-6 text-center">
          <h1 className="font-bold text-4xl text-gray-800 uppercase tracking-wider">
            FREEDOM Sole Co.Ltd
          </h1>
          <p className="mt-2 text-gray-600 text-lg">ສັນຍາ (Contract)</p>
          <div className="mx-auto mt-4 h-1 w-24 bg-gray-800" />
        </div>

        <div className="flex items-start justify-between">
          <div className="text-sm">
            <p className="font-semibold text-gray-800">ຊື່ສັນຍາ:</p>
            <p className="mt-1">{contract.title}</p>
            {contract.contractNumber && (
              <>
                <p className="mt-2 font-semibold text-gray-800">ເລກທີສັນຍາ:</p>
                <p className="mt-1">{contract.contractNumber}</p>
              </>
            )}
          </div>
          <div className="text-right text-sm">
            <p className="font-semibold text-gray-800">ວັນທີ:</p>
            <p className="mt-1">{formatDateLocal(new Date())}</p>
          </div>
        </div>
      </div>

      {/* Contract Details */}
      <div className="space-y-6">
        {/* General Information */}
        <section>
          <h2 className="mb-4 border-b border-gray-300 pb-2 font-bold text-xl text-gray-800">
            ຂໍ້ມູນທົ່ວໄປ
          </h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-semibold text-gray-700">ປະເພດສັນຍາ:</p>
              <p className="mt-1">{contract.typeName || "-"}</p>
            </div>
            <div>
              <p className="font-semibold text-gray-700">ສະຖານະ:</p>
              <p className="mt-1">{contract.status}</p>
            </div>
            <div>
              <p className="font-semibold text-gray-700">ຄູ່ສັນຍາ:</p>
              <p className="mt-1">{contract.counterparty}</p>
            </div>
            {contract.counterpartyContact && (
              <div>
                <p className="font-semibold text-gray-700">ຂໍ້ມູນຕິດຕໍ່:</p>
                <p className="mt-1">{contract.counterpartyContact}</p>
              </div>
            )}
          </div>
        </section>

        {/* Schedule & Terms */}
        <section>
          <h2 className="mb-4 border-b border-gray-300 pb-2 font-bold text-xl text-gray-800">
            ລະຍະເວລາ & ເງື່ອນໄຂ
          </h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-semibold text-gray-700">ວັນທີເລີ່ມ:</p>
              <p className="mt-1">{formatDateLocal(contract.startDate)}</p>
            </div>
            <div>
              <p className="font-semibold text-gray-700">ວັນທີຫມົດອາຍຸ:</p>
              <p className="mt-1">{formatDateLocal(contract.endDate)}</p>
            </div>
            {contract.durationDays && (
              <div>
                <p className="font-semibold text-gray-700">ລະຍະເວລາ:</p>
                <p className="mt-1">{Math.round(contract.durationDays / 30)} ເດືອນ</p>
              </div>
            )}
            <div>
              <p className="font-semibold text-gray-700">ຕໍ່ອາຍຸອັດຕະໂນມັດ:</p>
              <p className="mt-1">{contract.autoRenewal ? "ເປີດໃຊ້ງານ" : "ປິດໃຊ້ງານ"}</p>
            </div>
            <div>
              <p className="font-semibold text-gray-700">ໄລຍະເວລາແຈ້ງເຕືອນ:</p>
              <p className="mt-1">{contract.renewalNoticeDays} ວັນກ່ອນຫມົດອາຍຸ</p>
            </div>
          </div>
        </section>

        {/* Administration */}
        {(contract.employeeName ||
          contract.responsiblePersonName ||
          contract.departmentName) && (
          <section>
            <h2 className="mb-4 border-b border-gray-300 pb-2 font-bold text-xl text-gray-800">
              ການບໍລິຫານ
            </h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {contract.employeeName && (
                <div>
                  <p className="font-semibold text-gray-700">ພະນັກງານທີ່ຜູກກັບສັນຍາ:</p>
                  <p className="mt-1">{contract.employeeName}</p>
                </div>
              )}
              {contract.responsiblePersonName && (
                <div>
                  <p className="font-semibold text-gray-700">ຜູ້ຮັບຜິດຊອບ:</p>
                  <p className="mt-1">{contract.responsiblePersonName}</p>
                </div>
              )}
              {contract.departmentName && (
                <div>
                  <p className="font-semibold text-gray-700">ພະແນກ:</p>
                  <p className="mt-1">{contract.departmentName}</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Special Conditions */}
        {contract.specialConditions && (
          <section>
            <h2 className="mb-4 border-b border-gray-300 pb-2 font-bold text-xl text-gray-800">
              ເງື່ອນໄຂພິເສດ
            </h2>
            <p className="text-sm leading-relaxed">{contract.specialConditions}</p>
          </section>
        )}

        {/* Notes */}
        {contract.notes && (
          <section>
            <h2 className="mb-4 border-b border-gray-300 pb-2 font-bold text-xl text-gray-800">
              ຫມາຍເຫດສຳຄັນ
            </h2>
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{contract.notes}</p>
          </section>
        )}

        {/* Warnings */}
        {contract.warnings && (
          <section>
            <h2 className="mb-4 border-b border-orange-500 pb-2 font-bold text-xl text-orange-700">
              ຂໍ້ຄວນລະວັງ
            </h2>
            <p className="text-sm leading-relaxed whitespace-pre-wrap text-orange-800">{contract.warnings}</p>
          </section>
        )}
      </div>

      {/* Footer */}
      <div className="mt-12 border-t border-gray-300 pt-6 text-center text-xs text-gray-600 print:mt-8">
        <p>ເອກະສານນີ້ຖືກສ້າງຂຶ້ນເມື່ອ {formatDateTimeLocal(contract.updatedAt)}</p>
      </div>
    </div>
  );
}
